import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import FramedAvatar from '../components/FramedAvatar';
import { toast } from 'sonner';
import errMsg from '../utils/apiError';
import BookmarkButton from '../components/BookmarkButton';
import RichTextEditor from '../components/RichTextEditor';
import RichContent from '../components/RichContent';
import VideoEmbed from '../components/VideoEmbed';
import { chapterLabel as chapterDisplayName, chapterOrder as chapterSortKey } from '../utils/chapterLabel';
import {
  ArrowLeft,
  HelpCircle,
  Plus,
  ThumbsUp,
  CheckCircle2,
  MessageSquare,
  Video,
  Clock,
  User,
  Bell,
  Send,
  RefreshCw,
  Trash2,
  BookOpen,
  X
} from 'lucide-react';

const QuestionsPage = () => {
  const { user, api, canModerate } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [answerOpen, setAnswerOpen] = useState(null);
  const [filter, setFilter] = useState('unanswered');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [chapters, setChapters] = useState([]);
  const [chapterFilter, setChapterFilter] = useState('all');
  const [newQuestion, setNewQuestion] = useState({
    question: '',
    category: 'general',
    chapter_id: null
  });
  const [answerData, setAnswerData] = useState({
    answer: '',
    answer_video_url: ''
  });

  useEffect(() => {
    fetchQuestions();
  }, [searchQuery]);

  useEffect(() => {
    if (searchParams.get('ask') === '1') {
      setCreateOpen(true);
      searchParams.delete('ask');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  useEffect(() => {
    api.get('/chapters')
      .then((res) => setChapters([...(res.data || [])].sort((a, b) => chapterSortKey(a) - chapterSortKey(b))))
      .catch(() => setChapters([]));
  }, []);

  const chapterLabel = (chapter) => chapterDisplayName(chapter);
  const countFor = (chapterId) =>
    questions.filter((q) => String(q.chapter_id || '') === String(chapterId)).length;

  const fetchQuestions = async () => {
    try {
      const params = searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : '';
      const response = await api.get(`/questions${params}`);
      setQuestions(response.data);
    } catch (error) {
      toast.error(errMsg(error, 'Erreur de chargement'));
    } finally {
      setLoading(false);
    }
  };

  const handleBumpQuestion = async (questionId) => {
    try {
      await api.post(`/questions/${questionId}/bump`);
      toast.success('🔔 Question relancée !');
      fetchQuestions();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };

  const handleCreateQuestion = async (e) => {
    e.preventDefault();
    if (!newQuestion.question) {
      toast.error('Posez votre question');
      return;
    }
    try {
      await api.post('/questions', newQuestion);
      toast.success('❓ Question soumise !');
      setCreateOpen(false);
      setNewQuestion({ question: '', category: 'general', chapter_id: null });
      fetchQuestions();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };

  const handleAddComment = async (questionId) => {
    if (!newComment.trim()) {
      toast.error('Écrivez votre commentaire');
      return;
    }
    try {
      const response = await api.post(`/questions/${questionId}/comments`, { content: newComment.trim() });
      if (response.data.reopened) {
        toast.success('🔄 Question relancée ! Les admins ont été notifiés.');
      } else {
        toast.success('💬 Commentaire ajouté !');
      }
      setNewComment('');
      fetchQuestions();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };

  const handleVote = async (questionId) => {
    try {
      const response = await api.post(`/questions/${questionId}/vote`);
      toast.success(response.data.voted ? '👍 Vote ajouté !' : 'Vote retiré');
      fetchQuestions();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Supprimer définitivement cette question ? Les commentaires seront également perdus.')) return;
    try {
      await api.delete(`/questions/${questionId}`);
      toast.success('🗑️ Question supprimée');
      fetchQuestions();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };

  const handleAnswer = async (questionId) => {
    if (!answerData.answer || !answerData.answer.replace(/<[^>]*>/g, '').trim()) {
      toast.error('Réponse requise');
      return;
    }
    try {
      await api.put(`/questions/${questionId}/answer`, {
        answer: answerData.answer,
        answer_video_url: answerData.answer_video_url || null
      });
      toast.success('✅ Réponse publiée !');
      setAnswerOpen(null);
      setAnswerData({ answer: '', answer_video_url: '' });
      fetchQuestions();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };

  const openAnswerDialog = (question, open) => {
    if (open) {
      setAnswerData({
        answer: question.answer || '',
        answer_video_url: question.answer_video_url || ''
      });
    }
    setAnswerOpen(open ? question.id : null);
  };

  const filteredQuestions = questions.filter(q => {
    if (chapterFilter === 'general' && q.chapter_id) return false;
    if (chapterFilter !== 'all' && chapterFilter !== 'general' && String(q.chapter_id || '') !== chapterFilter) return false;
    if (filter === 'answered') return q.is_answered;
    if (filter === 'unanswered') return !q.is_answered;
    return true;
  });

  const activeChapterLabel = (() => {
    if (chapterFilter === 'all') return 'Toutes les questions';
    if (chapterFilter === 'general') return 'Questions générales (hors chapitre)';
    const c = chapters.find((ch) => String(ch.id) === chapterFilter);
    return c ? chapterLabel(c) : 'Chapitre';
  })();

  const openCreateDialog = (open) => {
    if (open && chapterFilter !== 'all' && chapterFilter !== 'general') {
      setNewQuestion((prev) => ({ ...prev, chapter_id: chapterFilter }));
    }
    setCreateOpen(open);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <HelpCircle className="w-12 h-12 text-primary animate-pulse mx-auto mb-4" />
          <p className="text-muted-foreground">Chargement des questions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="questions-page">
      <div className="noise-overlay" />

      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                <HelpCircle className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-xl">Q&A de la formation</h1>
                <p className="text-sm text-muted-foreground">Tes questions sur les chapitres de formation, répondues par l'équipage</p>
              </div>
            </div>
          </div>

          <Dialog open={createOpen} onOpenChange={openCreateDialog}>
            <DialogTrigger asChild>
              <Button className="bg-purple-500 text-white hover:bg-purple-600 shrink-0 px-3 sm:px-4" data-testid="ask-question-btn">
                <Plus className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Poser une question</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-purple-500" />
                  Poser une question
                </DialogTitle>
                <DialogDescription>Les questions les plus votées seront traitées en priorité</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateQuestion} className="space-y-4">
                <div className="space-y-2 rounded-lg border border-primary/30 bg-secondary/30 p-3">
                  <Label className="font-semibold flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-primary" />
                    Sur quel chapitre porte ta question ? *
                  </Label>
                  <Select
                    value={newQuestion.chapter_id || 'general'}
                    onValueChange={(v) => setNewQuestion({ ...newQuestion, chapter_id: v === 'general' ? null : v })}
                  >
                    <SelectTrigger className="bg-card h-11" data-testid="question-chapter-select">
                      <SelectValue placeholder="Choisis un chapitre" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value="general">Question générale (hors chapitre)</SelectItem>
                      {chapters.map((c) => (
                        <SelectItem key={c.id} value={String(c.id)}>{chapterLabel(c)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Choisir le bon chapitre permet de retrouver ta question dans l'onglet correspondant.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Votre question *</Label>
                  <Textarea 
                    value={newQuestion.question} 
                    onChange={(e) => setNewQuestion({ ...newQuestion, question: e.target.value })} 
                    placeholder="Quelle est votre question..."
                    className="bg-secondary/50"
                    rows={4}
                    data-testid="question-input"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Annuler</Button>
                  <Button type="submit" className="bg-purple-500 text-white" data-testid="submit-question-btn">
                    <HelpCircle className="w-4 h-4 mr-2" />
                    Soumettre
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Input 
              placeholder="🔍 Rechercher une question (mots-clés)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-secondary/50 pr-20"
              data-testid="search-questions"
            />
            {searchQuery && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="absolute right-2 top-1/2 -translate-y-1/2"
                onClick={() => setSearchQuery('')}
              >
                Effacer
              </Button>
            )}
          </div>
          {searchQuery && (
            <p className="text-sm text-muted-foreground mt-2">
              {questions.length} résultat(s) pour "{searchQuery}"
            </p>
          )}
        </div>

        {/* Filtre par chapitre : onglets défilants sur ordinateur, liste déroulante sur mobile */}
        {chapters.length > 0 && (
          <div className="mb-6 rounded-xl border border-border bg-card p-4 shadow-sm" data-testid="chapter-filter-block">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <BookOpen className="w-4 h-4 text-primary shrink-0" />
                <h2 className="font-heading text-base font-semibold truncate">Filtrer par chapitre</h2>
              </div>
              {chapterFilter !== 'all' && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="shrink-0 text-xs h-7 px-2"
                  onClick={() => setChapterFilter('all')}
                  data-testid="chapter-filter-reset"
                >
                  <X className="w-3 h-3 mr-1" />
                  Tout afficher
                </Button>
              )}
            </div>
            <div className="filet-gold my-3" />
            <p className="text-xs text-muted-foreground mb-3 break-words" data-testid="chapter-filter-active">
              <span className="font-semibold text-foreground">{activeChapterLabel}</span>
              {' · '}
              {filteredQuestions.length} question{filteredQuestions.length > 1 ? 's' : ''} affichée{filteredQuestions.length > 1 ? 's' : ''}
            </p>

            <div className="hidden md:block overflow-x-auto pb-2 -mx-1 px-1" data-testid="chapter-filter">
              <div className="flex items-center gap-2 w-max">
                {[{ id: 'all', title: 'Toutes les questions' }, { id: 'general', title: 'Questions générales' }, ...chapters].map((c) => {
                  const value = String(c.id);
                  const isActive = chapterFilter === value;
                  const label = c.id === 'all' || c.id === 'general' ? c.title : chapterLabel(c);
                  const count = c.id === 'all'
                    ? questions.length
                    : c.id === 'general'
                      ? questions.filter((q) => !q.chapter_id).length
                      : countFor(c.id);
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setChapterFilter(value)}
                      className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        isActive
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-secondary/40 border-border hover:border-primary/40'
                      }`}
                      data-testid={`chapter-tab-${value}`}
                    >
                      {label}
                      <span className={`ml-2 inline-block rounded-full px-1.5 py-0.5 text-[10px] leading-none ${isActive ? 'bg-white/20' : 'bg-foreground/10 text-muted-foreground'}`}>{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="md:hidden">
              <Select value={chapterFilter} onValueChange={setChapterFilter}>
                <SelectTrigger className="bg-secondary/50 h-11 text-sm" data-testid="chapter-filter-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">Toutes les questions ({questions.length})</SelectItem>
                  <SelectItem value="general">Questions générales ({questions.filter((q) => !q.chapter_id).length})</SelectItem>
                  {chapters.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {chapterLabel(c)} ({countFor(c.id)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Filters */}
        <Tabs value={filter} onValueChange={setFilter} className="mb-6">
          <TabsList className="bg-secondary/50">
            <TabsTrigger value="unanswered">
              <Clock className="w-4 h-4 mr-1" />
              En attente ({questions.filter(q => !q.is_answered).length})
            </TabsTrigger>
            <TabsTrigger value="answered">
              <CheckCircle2 className="w-4 h-4 mr-1" />
              Répondues ({questions.filter(q => q.is_answered).length})
            </TabsTrigger>
            <TabsTrigger value="all">Toutes</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Questions List */}
        {filteredQuestions.length === 0 ? (
          <Card className="card-base text-center py-12">
            <CardContent>
              <HelpCircle className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-heading text-xl mb-2">
                {filter === 'unanswered' ? 'Aucune question en attente' : 'Aucune question'}
              </h3>
              <p className="text-muted-foreground mb-4">
                {filter === 'unanswered' ? 'Toutes les questions ont été traitées !' : 'Soyez le premier à poser une question !'}
              </p>
              {filter === 'unanswered' && (
                <Button onClick={() => openCreateDialog(true)} className="bg-purple-500 text-white hover:bg-purple-600">
                  <Plus className="w-4 h-4 mr-2" />
                  Poser une question
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredQuestions.sort((a, b) => b.vote_count - a.vote_count).map((question) => (
              <Card key={question.id} className={`card-base ${question.is_answered ? 'border-green-500/20' : ''}`} data-testid={`question-${question.id}`}>
                <CardContent className="p-3 sm:p-4 overflow-hidden">
                  <div className="flex gap-3 sm:gap-4 min-w-0">
                    {/* Vote Button */}
                    <div className="flex flex-col items-center shrink-0">
                      <Button 
                        variant={question.votes?.includes(user.id) ? "default" : "outline"}
                        size="sm"
                        className={`h-auto py-2 px-3 ${question.votes?.includes(user.id) ? 'bg-purple-500 text-white' : ''}`}
                        onClick={() => handleVote(question.id)}
                        data-testid={`vote-question-${question.id}`}
                      >
                        <ThumbsUp className={`w-4 h-4 ${question.votes?.includes(user.id) ? 'fill-current' : ''}`} />
                      </Button>
                      <span className="text-lg font-bold mt-1">{question.vote_count || 0}</span>
                    </div>

                    {/* Question Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 flex-wrap mb-2">
                        <div className="flex items-center gap-2 flex-wrap min-w-0">
                          <Badge variant="outline" className="max-w-full truncate" data-testid={`question-chapter-badge-${question.id}`}>
                            {question.chapter_title || 'Question générale'}
                          </Badge>
                          {question.is_answered && (
                            <Badge className="bg-green-500/20 text-green-500">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Répondue
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0 ml-auto">
                        <span className="text-xs text-muted-foreground">
                          {new Date(question.created_at).toLocaleDateString('fr-FR')}
                        </span>
                        <BookmarkButton
                          itemType="question"
                          itemId={question.id}
                          itemTitle={question.question}
                          itemPreview={question.answer || ''}
                          itemUrl="/questions"
                        />
                        </div>
                      </div>

                      <p className="font-medium mb-2 break-words">{question.question}</p>

                      <div className="flex items-center gap-2 text-sm text-muted-foreground min-w-0">
                        <User className="w-3 h-3" />
                        <span>{question.user_name}</span>
                      </div>

                      {/* Answer Section */}
                      {question.is_answered && (
                        <div className="mt-4 p-4 bg-green-500/5 border border-green-500/20 rounded-lg min-w-0" data-testid={`answer-${question.id}`}>
                          <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                            <div className="flex items-center gap-2 text-green-500 min-w-0">
                              <CheckCircle2 className="w-4 h-4 shrink-0" />
                              <span className="font-medium break-words">
                                Réponse officielle
                                {question.answered_by_name ? ` — ${question.answered_by_name}` : ''}
                              </span>
                            </div>
                            {question.answered_at && (
                              <span className="text-xs text-muted-foreground shrink-0">
                                {new Date(question.answered_at).toLocaleDateString('fr-FR')}
                              </span>
                            )}
                          </div>
                          <RichContent html={question.answer || ''} className="text-sm break-words" />
                          {question.answer_video_url && (
                            <VideoEmbed
                              url={question.answer_video_url}
                              className="mt-3"
                              testId={`answer-video-${question.id}`}
                            />
                          )}
                        </div>
                      )}

                      {/* Comments Section */}
                      {question.comments && question.comments.length > 0 && (
                        <div className="mt-4 border-t border-border pt-4">
                          <button 
                            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-2 mb-3"
                            onClick={() => setExpandedQuestion(expandedQuestion === question.id ? null : question.id)}
                          >
                            <MessageSquare className="w-4 h-4" />
                            {question.comments.length} commentaire{question.comments.length > 1 ? 's' : ''}
                            <span className="text-xs">{expandedQuestion === question.id ? '▲' : '▼'}</span>
                          </button>
                          
                          {expandedQuestion === question.id && (
                            <div className="space-y-3 pl-4 border-l-2 border-muted">
                              {question.comments.map((comment) => (
                                <div key={comment.id} className="flex gap-2">
                                  <FramedAvatar
                                    src={comment.user_avatar}
                                    fallback={comment.user_name?.split(' ').map(n => n[0]).join('')}
                                    role={comment.user_role}
                                    size="sm"
                                  />
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-medium">{comment.user_name}</span>
                                      {comment.user_role === 'admin' && <Badge variant="outline" className="text-xs">Admin</Badge>}
                                      <span className="text-xs text-muted-foreground">
                                        {new Date(comment.created_at).toLocaleDateString('fr-FR')}
                                      </span>
                                    </div>
                                    <p className="text-sm text-muted-foreground mt-1">{comment.content}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Add Comment Input */}
                      <div className="mt-4 pt-4 border-t border-border">
                        <div className="flex gap-2 min-w-0">
                          <Input 
                            placeholder={question.is_answered && question.user_id === user.id 
                              ? "Tu as une question de suivi ? (Relance la question)" 
                              : "Ajouter un commentaire..."
                            }
                            value={expandedQuestion === question.id ? newComment : ''}
                            onChange={(e) => {
                              setExpandedQuestion(question.id);
                              setNewComment(e.target.value);
                            }}
                            onFocus={() => setExpandedQuestion(question.id)}
                            className="bg-secondary/50 text-sm min-w-0 flex-1"
                          />
                          <Button 
                            size="sm" 
                            onClick={() => handleAddComment(question.id)}
                            disabled={!newComment.trim() || expandedQuestion !== question.id}
                            className="bg-primary shrink-0"
                          >
                            <Send className="w-4 h-4" />
                          </Button>
                        </div>
                        {question.is_answered && question.user_id === user.id && (
                          <p className="text-xs text-orange-500 mt-1 flex items-center gap-1">
                            <RefreshCw className="w-3 h-3" />
                            Ton commentaire relancera la question en "En attente"
                          </p>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center flex-wrap gap-2 mt-4">
                        {/* Admin Answer Button */}
                        {canModerate && (
                          <Dialog open={answerOpen === question.id} onOpenChange={(open) => openAnswerDialog(question, open)}>
                            <DialogTrigger asChild>
                              <Button variant="outline" size="sm" data-testid={`answer-question-${question.id}`}>
                                <MessageSquare className="w-4 h-4 mr-2" />
                                {question.is_answered ? 'Modifier la réponse' : 'Répondre officiellement'}
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="bg-card border-border w-[min(820px,94vw)] max-h-[88vh] overflow-y-auto">
                              <DialogHeader>
                                <DialogTitle className="font-heading">
                                  {question.is_answered ? 'Modifier la réponse officielle' : 'Répondre à la question'}
                                </DialogTitle>
                                <DialogDescription className="line-clamp-2">{question.question}</DialogDescription>
                              </DialogHeader>
                              <div className="space-y-4">
                                <div className="space-y-2">
                                  <Label>Réponse * (texte mis en forme)</Label>
                                  <RichTextEditor
                                    value={answerData.answer}
                                    onChange={(html) => setAnswerData({ ...answerData, answer: html })}
                                    rows={10}
                                    placeholder="Votre réponse... (gras, listes, titres, liens)"
                                    testId={`answer-editor-${question.id}`}
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label className="flex items-center gap-2">
                                    <Video className="w-4 h-4 text-primary" />
                                    Vidéo de réponse (optionnel)
                                  </Label>
                                  <Input 
                                    value={answerData.answer_video_url} 
                                    onChange={(e) => setAnswerData({ ...answerData, answer_video_url: e.target.value })} 
                                    placeholder="https://youtube.com/watch?v=... , Vimeo ou Loom"
                                    className="bg-secondary/50"
                                    data-testid={`answer-video-input-${question.id}`}
                                  />
                                  <p className="text-xs text-muted-foreground">
                                    YouTube, Vimeo et Loom sont lus directement dans la page. Tout autre lien s'affichera en bouton.
                                  </p>
                                  {answerData.answer_video_url && (
                                    <VideoEmbed url={answerData.answer_video_url} className="mt-2" testId="answer-video-preview" />
                                  )}
                                </div>
                                <DialogFooter>
                                  <Button variant="outline" onClick={() => setAnswerOpen(null)}>Annuler</Button>
                                  <Button onClick={() => handleAnswer(question.id)} className="bg-green-500 text-white" data-testid={`publish-answer-${question.id}`}>
                                    <CheckCircle2 className="w-4 h-4 mr-2" />
                                    Publier
                                  </Button>
                                </DialogFooter>
                              </div>
                            </DialogContent>
                          </Dialog>
                        )}
                        
                        {/* Bump Button for unanswered questions */}
                        {!question.is_answered && !canModerate && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-orange-500 hover:text-orange-600 hover:bg-orange-500/10"
                            onClick={() => handleBumpQuestion(question.id)}
                            data-testid={`bump-question-${question.id}`}
                          >
                            <Bell className="w-4 h-4 mr-2" />
                            Relancer ({question.bump_count || 0})
                          </Button>
                        )}

                        {/* Delete Button for moderators */}
                        {canModerate && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-500/10 sm:ml-auto"
                            onClick={() => handleDeleteQuestion(question.id)}
                            data-testid={`delete-question-${question.id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Supprimer
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default QuestionsPage;
